<template>
  <div class="space-y-4">
    <section class="app-section">
      <div class="app-toolbar items-center justify-between">
        <h2 class="m-0 text-xl font-semibold">{{ videosTitle }}</h2>
      </div>

      <div class="app-toolbar">
        <select class="input-field-sm" v-model="limit" @change="fetchVideos">
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
      </div>

      <div
        class="upload-area"
        @click="triggerFileInput"
        @dragover.prevent
        @drop.prevent="handleFileDrop"
        :class="{ uploading: isUploading }"
      >
        <input
          type="file"
          ref="fileInput"
          @change="handleFileSelect"
          accept="video/*"
          style="display: none;"
        />
        <p v-if="!isUploading">Перетащите видео сюда или кликните для выбора</p>

        <div v-if="isUploading" class="upload-progress">
          <p>Загрузка видео...</p>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
          </div>
          <span class="progress-text">{{ uploadProgress }}%</span>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="app-table min-w-[980px]">
          <thead>
            <tr>
              <th>Название</th>
              <th>Статус</th>
              <th>Прогресс</th>
              <th>Создано</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="video in videos" :key="video.id" :class="{ 'new-video': isNewVideo(video.updated_at) }">
              <td>{{ video.original_filename }}</td>
              <td>
                <span class="status-badge" :class="getStatusClass(video.status)">
                  {{ getStatusText(video.status) }}
                </span>
              </td>
              <td>
                <div class="progress-container">
                  <div class="progress-bar-inline" :style="{ width: video.progress + '%' }"></div>
                  <span class="progress-text-inline">{{ video.progress }}%</span>
                </div>
              </td>
              <td>{{ formatDate(video.created_at) }}</td>
              <td>
                <div class="flex flex-wrap gap-2">
                  <button class="button button-outline !h-8 !px-3 !py-0 text-xs" @click="copyToClipboard(video.id)">UUID</button>
                  <button v-if="video.playlist_url" class="button button-outline !h-8 !px-3 !py-0 text-xs" @click="copyPlaylistLink(video.playlist_url)">Ссылка</button>
                  <button v-if="video.status === 'failed'" class="button button-outline !h-8 !px-3 !py-0 text-xs" @click="showError(video.error_message)">Ошибка</button>
                  <button class="button button-danger !h-8 !px-3 !py-0 text-xs" @click="deleteVideo(video.id)">Удалить</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="notification" class="notification">{{ notification }}</div>
      <div class="pagination">
        <button class="button button-outline" @click="prevPage" :disabled="skip === 0">Назад</button>
        <button class="button button-outline" @click="nextPage">Вперед</button>
      </div>
    </section>
  </div>
</template>

<script>
import { uploadVideo, fetchVideos, fetchMyVideos, deleteVideoApi } from '@/services/api';
import { isUserAdminForUi } from '../utils/user-role-override';

export default {
  name: 'VideosView',
  props: {
    currentUser: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    canManageAll() {
      return Boolean(this.currentUser?.is_superuser) || isUserAdminForUi(this.currentUser);
    },
    videosTitle() {
      return this.canManageAll ? 'Видео' : 'Мои видео';
    }
  },
  watch: {
    currentUser: {
      deep: true,
      handler() {
        this.fetchVideos();
      }
    }
  },
  data() {
    return {
      videos: [],
      notification: '',
      skip: 0,
      limit: 10,
      intervalId: null,
      isUploading: false,
      uploadProgress: 0,
    };
  },
  methods: {
    async fetchVideos() {
      try {
        this.videos = this.canManageAll
          ? await fetchVideos(this.skip, this.limit)
          : await fetchMyVideos(this.skip, this.limit);
      } catch (error) {
        console.error('Ошибка при получении списка видео:', error);
      }
    },
    formatDate(inputDate) {
      const now = new Date();
      const date = new Date(inputDate);
      const diffInSeconds = Math.floor((now - date) / 1000);
      
      if (diffInSeconds < 60) {
        return 'меньше минуты назад';
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes} мин назад`;
      } else if (date.toDateString() === now.toDateString()) {
        return `Сегодня, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else if (date.toDateString() === new Date(now - 86400000).toDateString()) {
        return `Вчера, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else {
        const options = { day: 'numeric', month: 'long' };
        const formattedDate = date.toLocaleDateString('ru-RU', options);
        const year = date.getFullYear() !== now.getFullYear() ? `, ${date.getFullYear()}` : '';
        return `${formattedDate}${year}`;
      }
    },
    isNewVideo(inputDate) {
      const now = new Date();
      const date = new Date(inputDate);
      const diffInSeconds = Math.floor((now - date) / 1000);
      return diffInSeconds < 60;
    },
    getStatusText(status) {
      const statusMap = {
        'uploading': 'Загружается',
        'processing': 'Обрабатывается',
        'completed': 'Завершено',
        'failed': 'Ошибка'
      };
      return statusMap[status] || status;
    },
    getStatusClass(status) {
      return `status-${status}`;
    },
    copyToClipboard(uuid) {
      navigator.clipboard.writeText(uuid);
      this.showNotification('UUID скопирован в буфер обмена');
    },
    copyPlaylistLink(link) {
      navigator.clipboard.writeText(link);
      this.showNotification('Ссылка на плейлист скопирована в буфер обмена');
    },
    showError(errorMessage) {
      this.showNotification(`Ошибка: ${errorMessage}`);
    },
    showNotification(message) {
      this.notification = message;
      const readingTime = Math.ceil(message.length / 1500 * 60) + 1;
      setTimeout(() => {
        this.notification = '';
      }, readingTime * 1000);
    },
    nextPage() {
      this.skip = Number(this.skip) + Number(this.limit);
      this.fetchVideos();
    },
    prevPage() {
      if (this.skip > 0) {
        this.skip -= this.limit;
        this.fetchVideos();
      }
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadVideo(file);
      }
    },
    handleFileDrop(event) {
      const file = event.dataTransfer.files[0];
      if (file) {
        this.uploadVideo(file);
      }
    },
    async uploadVideo(file) {
      try {
        this.isUploading = true;
        this.uploadProgress = 0;
        
        await uploadVideo(
          file,
          (progress) => {
            this.uploadProgress = progress;
          }
        );
        
        this.showNotification('Видео успешно загружено, начинается обработка...');
        await this.fetchVideos();
        this.uploadProgress = 0;
        this.isUploading = false;
        
      } catch (error) {
        console.error(error);
        this.showNotification('Ошибка при загрузке видео: ' + (error.message || 'Неизвестная ошибка'));
        this.uploadProgress = 0;
        this.isUploading = false;
      }
    },
    async deleteVideo(uuid) {
      try {
        await deleteVideoApi(uuid);
        this.showNotification('Видео успешно удалено');
        await this.fetchVideos();
      } catch (error) {
        console.error('Ошибка при удалении видео:', error);
        this.showNotification(`${error}`);
      }
    },
  },
  mounted() {
    this.fetchVideos();
    this.intervalId = setInterval(this.fetchVideos, 2000);
  },
  beforeUnmount() {
    clearInterval(this.intervalId);
  }
}
</script>

<style scoped>
.notification {
  margin-top: 10px;
  padding: 10px;
  background-color: hsl(var(--success));
  color: hsl(var(--success-foreground));
  border-radius: 5px;
  font-size: 14px;
}
.pagination {
  margin-top: 12px;
  display: flex;
  gap: 8px;
}
.new-video {
  box-shadow: inset 3px 0 0 hsl(var(--warning));
}
.upload-area {
  border: 2px dashed hsl(var(--border));
  padding: 20px;
  text-align: center;
  cursor: pointer;
  margin: 20px 0;
  border-radius: 8px;
  transition: all 0.3s ease;
  @apply bg-muted/50 text-foreground;
}
.upload-area:hover {
  border-color: hsl(var(--primary));
  background-color: hsl(var(--primary) / 0.08);
}
.upload-area.uploading {
  border-color: hsl(var(--primary));
  background-color: hsl(var(--primary) / 0.05);
  cursor: default;
}
.upload-progress {
  margin-top: 10px;
  text-align: center;
}
.upload-progress p {
  margin: 0 0 10px 0;
  @apply text-foreground;
  font-weight: 500;
}
.progress-bar {
  height: 8px;
  @apply bg-muted;
  border-radius: 4px;
  overflow: hidden;
  margin: 10px 0;
}
.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary) / 0.7));
  border-radius: 4px;
  transition: width 0.3s ease;
}
.progress-text {
  font-weight: 500;
  @apply text-muted-foreground;
  font-size: 14px;
}
.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}
.status-uploading {
  background-color: hsl(var(--warning) / 0.2);
  color: hsl(var(--warning-foreground));
}
.status-processing {
  background-color: hsl(var(--primary) / 0.2);
  color: hsl(var(--primary));
}
.status-completed {
  background-color: hsl(var(--success) / 0.2);
  color: hsl(var(--success));
}
.status-failed {
  background-color: hsl(var(--destructive) / 0.2);
  color: hsl(var(--destructive));
}
.progress-container {
  position: relative;
  width: 100px;
  height: 20px;
  @apply bg-muted;
  border-radius: 10px;
  overflow: hidden;
}
.progress-bar-inline {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary) / 0.7));
  transition: width 0.3s ease;
}
.progress-text-inline {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 11px;
  font-weight: 600;
  z-index: 1;
}
</style>
